import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  ChevronRight, Globe, LineChart, Clock, Calendar, TrendingUp, Search, Target,
  BarChart3, ArrowLeftRight, Medal, TrendingDown, Bitcoin, Shield
} from 'lucide-react';

const forexBasics = [
  { icon: Globe, title: 'Global Market', text: 'Forex trading is the process of buying and selling currencies in the global foreign exchange market.' },
  { icon: LineChart, title: 'Currency Pairs', text: 'It involves trading currency pairs to take advantage of exchange rate fluctuations and earn profits.' },
  { icon: Clock, title: '24/5 Market', text: 'The Forex market is the largest and most liquid financial market in the world, operating 24 hours a day, 5 days a week.' },
];

const strategies = [
  { icon: Calendar, title: 'Intraday Trading', desc: 'Positions are opened and closed on the same trading day.' },
  { icon: TrendingUp, title: 'Swing Trading', desc: 'Trades held for several days or weeks to benefit from medium-term market trends.' },
  { icon: Search, title: 'Scalping', desc: 'A fast-paced strategy involving multiple small trades throughout the day.' },
  { icon: Target, title: 'Position Trading', desc: 'A long-term approach where trades are held for weeks, months, or even years.' },
];

const services = [
  { icon: BarChart3, title: 'Stock Trading', desc: 'Buy & Sell company shares for profit.' },
  { icon: ArrowLeftRight, title: 'Forex Trading', desc: 'Trade global currency pairs (EUR/USD, GBP/JPY).' },
  { icon: Bitcoin, title: 'Cryptocurrency', desc: 'Invest in Bitcoin, Ethereum & other digital assets.' },
  { icon: Medal, title: 'Commodity Trading', desc: 'Gold, Silver, Crude Oil, Natural Gas & Agricultural Products.' },
  { icon: TrendingDown, title: 'Index Trading', desc: 'Trade market indices for diversified investment opportunities.' },
];

export default function Services() {
  return (
    <>
      <Navbar />

      {/* PAGE BANNER */}
      <section className="page-banner">
        <div className="page-banner-bg" style={{ backgroundImage: 'url(/mlm2.png)' }} />
        <div className="page-banner-overlay" />
        <div className="container page-banner-content">
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="breadcrumb-item">Home</Link>
            <ChevronRight size={14} className="breadcrumb-sep" />
            <span className="breadcrumb-item breadcrumb-current">Services</span>
          </nav>
          <h1 className="page-banner-title">
            TRADING & <span style={{ color: 'var(--primary-light, #6fa0ff)' }}>SERVICES</span>
          </h1>
          <p className="page-banner-sub">
            Trading is the process of buying and selling financial assets to generate profits from market price movements — across every major asset class.
          </p>
        </div>
      </section>

      {/* WHAT IS FOREX */}
      <section className="section" id="forex">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">WHAT IS <span>FOREX TRADING?</span></h2>
            <div className="section-line"></div>
          </div>
          <div className="grid-3">
            {forexBasics.map(item => (
              <div key={item.title} className="info-box">
                <div className="info-box-icon"><item.icon size={32} /></div>
                <div className="info-box-title">{item.title}</div>
                <p className="info-box-text">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRADING STRATEGIES */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">TRADING <span>STRATEGIES</span></h2>
            <div className="section-line"></div>
          </div>
          <div className="grid-2">
            {strategies.map(s => (
              <div key={s.title} className="income-type-card">
                <div className="income-type-icon"><s.icon size={24} /></div>
                <div>
                  <div className="income-type-name">{s.title}</div>
                  <div className="income-type-desc">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24 }} className="card">
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <Shield size={32} style={{ flexShrink: 0, color: 'var(--primary)' }} />
              <p style={{ color: 'var(--text-light)' }}>
                <strong style={{ color: 'var(--primary)' }}>Successful trading requires</strong> market knowledge, technical analysis, disciplined risk management, and a well-planned strategy. Choose the right trading style for consistent long-term growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TRADING SERVICES */}
      <section className="section" id="services-list">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">WHAT WE <span>TRADE</span></h2>
            <div className="section-line"></div>
            <p className="section-desc">Multiple financial markets, one disciplined process.</p>
          </div>
          <div className="grid-3">
            {services.map(s => (
              <div key={s.title} className="info-box" style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: 'var(--primary)' }}><s.icon size={40} /></div>
                <div className="info-box-title">{s.title}</div>
                <p className="info-box-text">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-relative" style={{ background: 'linear-gradient(135deg, rgba(var(--secondary-rgb),0.1), rgba(var(--primary-rgb),0.08))' }}>
        <div className="glow-orb-field">
          <div className="glow-orb glow-orb-gold" style={{ top: 'auto', bottom: -80, left: '10%' }}></div>
          <div className="glow-orb glow-orb-blue" style={{ top: -60, right: '15%', bottom: 'auto' }}></div>
        </div>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Rajdhani', fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>
            READY TO <span style={{ color: 'var(--primary)' }}>START TRADING?</span>
          </h2>
          <p style={{ color: 'var(--text-light)', marginBottom: 32, maxWidth: 500, margin: '0 auto 32px' }}>
            Join thousands of investors building their financial future with Triveni Pay. Start with just ₹500 today.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" className="btn btn-primary btn-lg">Join Now It's Free</Link>
            <Link to="/login" className="btn btn-outline btn-lg">Login</Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}